import React, { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { get, map, filter, find } from 'lodash';
import { RootState } from 'modules';
import moment, { Moment } from 'moment';
import momentTZ from 'moment-timezone';
import { getAvailableSchedule } from 'libs/api/mentoring';
import { useDataApi } from 'libs/hooks';
import { useTimezone } from 'libs/hooks';
import { TAvailableSchedule, TSelectedTimeSlice } from 'modules/mentoring';
import { TMentor } from 'modules/user';
import { CDateFormat, CDayOfWeek } from 'constants/base.const';
import { CScheduleStatus } from 'constants/mentoring.const';
import { EScheduleStatus } from 'enums/mentoring.enum';
import { Col, Row, Modal, Button, DatePicker, Typography, Tag, Table, message } from 'antd';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { SecondaryColor, PrimaryColor, size } from 'GlobalStyles';

type TClassScheduleModalProps = {
  numOfSlice?: number;
  mentor: TMentor;
  visible: boolean;
  setVisible: any;
  selectedSchedules?: Array<Moment>;
  onFinish?: (data: Array<TSelectedTimeSlice>) => void;
  forUpdateMentoringClassId?: number; // 일정변경
};
const { Title, Text } = Typography;
const ColumnTitle = ({ date, day }: { date: number; day: string }) => {
  return (
    <>
      <div style={{ marginTop: '5px' }}>
        <Text>{day}</Text>
      </div>
      <div
        style={{
          color: `${PrimaryColor}`,
          fontWeight: 'bold',
          fontSize: 'calc(1vw + 16px)',
        }}
      >
        {date}
      </div>
    </>
  );
};
const CDataSelectedClass = 'data-selected';
const CActiveTableCellClass = 'table-cell-active';
const CCellHeight = 24;
const CNoMouseEventMaxModalsize = size.lg;

const ClassScheduleModal = ({
  numOfSlice,
  mentor,
  visible,
  setVisible,
  selectedSchedules,
  onFinish,
  forUpdateMentoringClassId,
}: TClassScheduleModalProps) => {
  const [getDateTime, timezone] = useTimezone();
  const today = getDateTime('');
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [currentDate, setCurrentDate] = useState(today);
  const [startDateOfWeek, setStartDateOfWeek] = useState(today.clone().day('Sunday'));
  const [selectedTimeSlices, setSelectedTimeSlices] = useState<Array<TSelectedTimeSlice>>([]);
  const getPromise = getAvailableSchedule.bind(null, get(mentor, 'id'), {
    date_from: startDateOfWeek
      .clone()
      .add(-1, 'days')
      .format(CDateFormat),
    date_to: startDateOfWeek
      .clone()
      .add(7, 'days')
      .format(CDateFormat),
    with_class_id: forUpdateMentoringClassId ? forUpdateMentoringClassId : '',
  });
  const [{ data, loading }, setCallback, setExecuting] = useDataApi<Array<TAvailableSchedule>>(
    getPromise,
    [],
    false,
  );
  const [tableRows, setTableRows] = useState<Array<TAvailableSchedule>>([]);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  // const [numOfSlice,setNumOfSlice] = useState<number>(0);

  const ref = useRef<HTMLLinkElement>(null);
  const [width, setWidth] = useState<number>(); //width 최대 1440

  useEffect(() => {
    if (ref.current) {
      const width = ref.current.offsetWidth;
      setWidth(width);
    }
  }, []);

  function handleCancel() {
    setVisible(false);
  }

  function handleSubmit() {
    try {
      setSubmitButtonDisabled(true);
      onFinish && onFinish(selectedTimeSlices);
      setVisible(false);
    } finally {
      setSubmitButtonDisabled(false);
    }
  }

  function setTargetCell(row: number, col: number) {
    const targetCell = document.querySelector(`div[data-row="${row}"][data-col="${col}"]`);
    const previewElem = document.getElementById(`selected-${row}-${col}`);
    if (targetCell) {
      targetCell.setAttribute(CDataSelectedClass, 'true');
    }
    if (previewElem) {
      previewElem.style.visibility = 'visible';
    }
  }

  useEffect(() => {
    if (!loading && data) {
      setTableRows([
        { time_slice: -2 },
        { time_slice: -1 },
        ...map(Array(48), (v, index) => {
          const rowData = find(get(data, 'schedules', []), (elem) => {
            return get(elem, 'time_slice') === index;
          });
          if (rowData) {
            return rowData;
          } else {
            return { time_slice: index };
          }
        }),
        { time_slice: 48 },
      ]);
    }
  }, [loading]);

  useEffect(() => {
    // 현재 날짜가 바뀌면 그날이 포함된 주로 focus 이동
    if (
      currentDate
        .clone()
        .day('Sunday')
        .week() != startDateOfWeek.week()
    ) {
      setStartDateOfWeek(currentDate.clone().day('Sunday'));
    }
  }, [currentDate]);

  useEffect(() => {
    // 최초 모달이 visible될때 backend에 요청보내서 가능한 스케줄 정보를 받아옴
    if (visible) {
      setCurrentDate(today);
      setCallback(() => getPromise);
      setExecuting(true);
      setSelectedTimeSlices(
        map(selectedSchedules, (v) => {
          return {
            time_slice: getTimeSliceFromDateTime(v),
            date_at: v.format(CDateFormat),
          };
        }),
      );
    }
  }, [visible]);

  useEffect(() => {
    setCallback(() => getPromise);
  }, [startDateOfWeek]);

  useEffect(() => {
    // 기준이 되는 주가 바뀌거나 특정 날짜를 선택하는 경우 그 cell들의 상태를 selected로 바꿔줌. selected를 다 지우고 다시 새로 추가함.
    map(document.querySelectorAll('div[data-row][data-col]'), (cell) => {
      cell.removeAttribute(CDataSelectedClass);
      const previewElem = document.getElementById(
        `selected-${cell.getAttribute('data-row')}-${cell.getAttribute('data-col')}`,
      );
      if (previewElem) {
        previewElem.style.visibility = 'hidden';
      }
    });
    map(selectedTimeSlices, (cell) => {
      if (moment(get(cell, 'date_at')).week() === currentDate.week()) {
        const weekDay = moment(get(cell, 'date_at')).weekday();
        setTargetCell(get(cell, 'time_slice'), weekDay);
        // 전날 오후 11시 이후로 선택한 경우 이어서 처리하는 부분
        if (get(cell, 'time_slice') >= 46) {
          const dataRow = Number(get(cell, 'time_slice')) - 48;
          const dataCol = Number(weekDay) + 1;
          setTargetCell(dataRow, dataCol);
        }
      } else if (
        moment(get(cell, 'date_at')).format(CDateFormat) ===
        startDateOfWeek
          .clone()
          .add(-1, 'days')
          .format(CDateFormat)
      ) {
        // 이전 주 마지막 날의 오후 11시 이후로 선택한 경우 처리
        const dataRow = Number(get(cell, 'time_slice')) - 48;
        const dataCol = startDateOfWeek.weekday();
        setTargetCell(dataRow, dataCol);
      }
    });
  }, [selectedTimeSlices, startDateOfWeek]);

  // useEffect(()=> {
  //     if(mentor?.mentoring_field === EMentoringField.Consulting){
  //       setNumOfSlice(CNumOfSlice) // 3
  //     }else {
  //       //멘토가 finance혹은 tech 멘토일 때
  //       setNumOfSlice(CNumOfOneHourSlice) // 2
  //     }
  // },[mentor])

  function onPanelChangeWeek(value: Moment, mode: string) {
    setCurrentDate(value);
  }

  function onChangeWeek(date: Moment | null, dateString: string) {
    if (date != null) {
      setCurrentDate(date);
    }
  }

  function handleClickCell(event: any) {
    const className = event.target.previousSibling?.getAttribute('class');
    if (className === EScheduleStatus.Available) {
      const timeSlice = event.target.getAttribute('data-row');
      const dateAt = startDateOfWeek.clone().add(event.target.getAttribute('data-col'), 'd');
      setCurrentDate(dateAt);
      //event.target.getAttribute(CDataSelectedClass) =>null or true
      if (event.target.getAttribute(CDataSelectedClass)) {
        // 체크된거 체크 해제
        setSelectedTimeSlices(
          filter(selectedTimeSlices, (v) => {
            return v.time_slice != timeSlice || v.date_at != dateAt.format(CDateFormat);
          }),
        );
      } else {
        let hasError = false;
        const timeOfSelectedTimeSlice = getDateTimeFromSelectedTimeSlice(
          {
            time_slice: timeSlice,
            date_at: dateAt.format(CDateFormat),
          },
          timezone,
        );
        find(Array(numOfSlice), (v, index) => {
          //⭐⭐⭐⭐⭐⭐
          const timeOfCurrentTimeSlice = timeOfSelectedTimeSlice.clone().add(30 * index, 'minutes');
          // 이미 선택된 시간대인지 체크
          const filteredSelectedTimeSlice = filter(selectedTimeSlices, (elem) => {
            const selectedTime = getDateTimeFromSelectedTimeSlice(
              {
                time_slice: elem.time_slice,
                date_at: elem.date_at,
              },
              timezone,
            );

            return (
              selectedTime <= timeOfCurrentTimeSlice &&
              numOfSlice &&
              timeOfCurrentTimeSlice < selectedTime.clone().add(30 * numOfSlice, 'minutes')
            );
          });
          if (filteredSelectedTimeSlice && filteredSelectedTimeSlice.length > 0) {
            message.error('이미 선택한 시간대입니다. 기존에 선택된 시간을 취소하고 다시 진행해주세요');
            hasError = true;

            return true;
          }
          // 해당 시간대가 Available한지 체크
          const filteredAvailableTimeSlice = find(get(data, 'schedules', []), (elem) => {
            return (
              getTimeSliceFromDateTime(timeOfCurrentTimeSlice) === get(elem, 'time_slice') &&
              get(elem, timeOfCurrentTimeSlice.format(CDateFormat)) === EScheduleStatus.Available
            );
          });
          if (!filteredAvailableTimeSlice) {
            message.error(`수업은 ${numOfSlice && numOfSlice * 30}분 단위로 선택 가능합니다`);
            hasError = true;

            return true;
          }
        });
        if (!hasError) {
          const previousSelected = selectedTimeSlices && !forUpdateMentoringClassId ? selectedTimeSlices : [];
          setSelectedTimeSlices([
            ...previousSelected,
            {
              time_slice: timeSlice,
              date_at: dateAt.format(CDateFormat),
            },
          ]);
        }
      }
    }
  }

  function getPreviewCellHeight(timeSlice: number) {
    if (timeSlice >= 46) {
      return CCellHeight * (48 - timeSlice) + 2;
    } else {
      return numOfSlice && CCellHeight * numOfSlice + 2;
    }
  }

  const columns = [
    {
      dataIndex: 'time_slice',
      width: 80,
      render: (value: string, row: TAvailableSchedule) => {
        if (get(row, 'time_slice') === -2) {
          return '';
        } else if (get(row, 'time_slice') % 2 === 0) {
          return {
            children: '',
            props: { rowSpan: 0 },
          };
        } else {
          return {
            children: (
              <span style={{ fontWeight: 'bold' }}>
                {/* 기존 */}
                {toLocaleString(getTimeFromTimeSlice(get(row, 'time_slice') + 1, timezone), timezone).replace(
                  ':00',
                  '시',
                )}
                {/* 수정 오전 0시로 표기*/}
                {/* {toLocaleString(getTimeFromTimeSlice(get(row, 'time_slice')+1, timezone), timezone).replace(':00', '시')==="오전 12시" ? "오전 0시" :toLocaleString(getTimeFromTimeSlice(get(row, 'time_slice')+1, timezone), timezone).replace(':00', '시')} */}
                {/* 0시, 01시~23시, 0시 */}
                {/* {toLocaleString(getTimeFromTimeSlice(get(row, 'time_slice')+1, timezone), timezone).replace(':00', '시')==="24시" ? "0시" : toLocaleString(getTimeFromTimeSlice(get(row, 'time_slice')+1, timezone), timezone).replace(':00', '시')} */}
              </span>
            ),
            props: { rowSpan: 2, className: 'row-border-none' },
          };
        }
      },
    },
    ...map(Array(7), (v, index) => {
      const columnDate = startDateOfWeek.clone().add(index, 'd');

      return {
        title: <ColumnTitle date={columnDate.date()} day={`${get(CDayOfWeek, index)}요일`} />,
        dataIndex: columnDate.format(CDateFormat),
        render: (dayStatus: EScheduleStatus, row: TAvailableSchedule) => {
          return (
            <>
              {get(row, 'time_slice') < 48 ? (
                <>
                  {/* 호버나 클릭하기 전 */}
                  <div className={dayStatus}>{CScheduleStatus[dayStatus]}</div>
                  {/* 호버나 클릭했을 경우 */}
                  <div
                    className={`${CActiveTableCellClass}`}
                    onClick={handleClickCell}
                    data-row={get(row, 'time_slice')}
                    data-col={index}
                    onMouseOver={() => {
                      if (width && width > CNoMouseEventMaxModalsize) {
                        const elem = document.getElementById(`hover-${get(row, 'time_slice')}-${index}`);
                        if (elem && dayStatus === EScheduleStatus.Available) {
                          elem.style.visibility = 'visible';
                        }
                      }
                    }}
                    onMouseOut={() => {
                      if (width && width > CNoMouseEventMaxModalsize) {
                        const elem = document.getElementById(`hover-${get(row, 'time_slice')}-${index}`);
                        if (elem) {
                          elem.style.visibility = 'hidden';
                        }
                      }
                    }}
                    style={{
                      overflow: 'hidden',
                      height: `${getPreviewCellHeight(get(row, 'time_slice'))}px`,
                    }}
                  >
                    <div
                      id={`hover-${get(row, 'time_slice')}-${index}`}
                      className='table-cell-preview'
                      style={{ pointerEvents: 'none', visibility: 'hidden' }} //onMouseOver, onMouseOut 여부에 따라서 visibility: 'hidden' or visibility: 'visible' 가 바뀐다
                    >
                      <img
                        className='small-thumbnail-img'
                        alt='thumbnail'
                        src={get(mentor, 'user.profile_image.absolute_url')}
                      />
                      <div>{get(mentor, 'user.fullname')}</div>
                    </div>

                    <div
                      id={`selected-${get(row, 'time_slice')}-${index}`}
                      className='table-cell-preview'
                      style={{
                        color: '#fff',
                        background: `${PrimaryColor}`,
                        pointerEvents: 'none',
                        visibility: 'hidden',
                      }}
                    >
                      <img
                        className='small-thumbnail-img'
                        alt='thumbnail'
                        src={get(mentor, 'user.profile_image.absolute_url')}
                      />
                      <div>선택완료</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={EScheduleStatus.UnAvailable}></div>
              )}
            </>
          );
        },
      };
    }),
  ];

  //selectedTimeSlices => 0: {time_slice: "0", date_at: "2021-08-19"}이 시간대부터 3개 타임(90분)씩 선택됨
  //tableRows => 각 시간별 time_slice와 가능한지 여부  ==>  3: {time_slice: 1, 2021-08-19: "available"}

  return (
    <div ref={ref as any}>
      <Modal
        visible={visible}
        maskClosable={true}
        closable={false}
        footer={
          <Row justify='end'>
            <Button onClick={handleCancel}>닫기</Button>
            <Button type='primary' onClick={handleSubmit}>
              저장
            </Button>
          </Row>
        }
        width={940}
        style={{ top: 10 }}
        onCancel={handleCancel}
      >
        <Row justify='space-between'>
          <Col style={{ height: '130px' }}>
            <span style={{ color: `${SecondaryColor}` }}>{timezone}</span>
            <div style={{ fontWeight: 'bold', fontSize: '30px' }}>
              {currentDate.year()}년 {currentDate.month() + 1}월
            </div>
            <div style={{ fontSize: '18px' }}>
              <Tag
                className='arrow-box'
                onClick={() => {
                  setCurrentDate(currentDate.clone().add(-7, 'd'));
                }}
              >
                <LeftOutlined />
              </Tag>
              <Tag
                style={{
                  background: `${PrimaryColor}`,
                  color: '#fff',
                  borderRadius: '8px',
                  height: '26px',
                  lineHeight: '26px',
                  padding: '0px 10px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setCurrentDate(today);
                }}
              >
                오늘
              </Tag>
              <Tag
                className='arrow-box'
                onClick={() => {
                  setCurrentDate(currentDate.clone().add(7, 'd'));
                }}
              >
                <RightOutlined />
              </Tag>
            </div>
          </Col>
          <Col style={{ height: '130px' }}>
            <DatePicker
              open={visible}
              onChange={onChangeWeek}
              onPanelChange={onPanelChangeWeek}
              picker='week'
              dropdownClassName='inline-datepicker-popup'
              value={currentDate}
              style={{ visibility: 'hidden', width: '300px' }}
              getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
              disabledDate={(current: Moment) => {
                return current && current < today.startOf('day');
              }}
              dateRender={(current) => {
                // 예약 가능한 일정이 있는 날짜는 강조표시
                const style = {} as { color: string };
                const isAvailable = find(get(data, 'available_dates', []), (available_at) => {
                  return current.format(CDateFormat) === available_at;
                });
                if (isAvailable) {
                  style.color = `${SecondaryColor}`;
                }
                return (
                  <div className='ant-picker-cell-inner' style={style}>
                    {current.date()}
                  </div>
                );
              }}
            />
          </Col>
        </Row>
        <ScheduleTableWrapper numOfSlice={numOfSlice}>
          <Table
            rowClassName={(record: TAvailableSchedule) =>
              get(record, 'time_slice') % 2 === 0 ? 'dashed' : ''
            }
            tableLayout={'fixed'}
            columns={columns}
            bordered={true}
            dataSource={tableRows}
            loading={loading}
            scroll={{ y: 350, scrollToFirstRowOnChange: false }}
            pagination={false}
          />
        </ScheduleTableWrapper>
      </Modal>
    </div>
  );
};

//moment.Moment 형태의 데이터에서 time_slice((h+m)*2) 정수값을 리턴해줌
export function getTimeSliceFromDateTime(date: Moment) {
  const hour = date.get('hour');
  const minute = date.get('minute');

  return hour * 2 + minute / 30;
}

//time_slice 정수값에서 momentTZ.Moment 형태의 time을 리턴
export function getTimeFromTimeSlice(value: string | Number, timezone: string) {
  const hour = Math.floor(Number(value) / 2);
  const minute = Number(value) % 2 === 1 ? 30 : 0;
  const time = momentTZ().tz(timezone);

  time.set({
    hour: hour,
    minute: minute,
    second: 0,
  });

  return time;
}

export function toLocaleString(dateTime: Moment, timezone?: string) {
  const formattingTime = dateTime.toDate().toLocaleString('ko-KR', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: timezone,
  });

  //기존
  // return formattingTime;

  //수정 오전 0시로 표기
  return formattingTime.includes('오전 12') ? formattingTime.replace('12', '0') : formattingTime;

  //옵션1 : 24시, 01시, 02시~ 23시, 24시
  // return dateTime.toDate().toLocaleString('ko-KR', {
  //   hour: 'numeric',
  //   minute: 'numeric',
  //   hour12: false,
  //   timeZone: timezone,
  // });
}

export function toLocaleStringLocal(dateTime: Moment, timezone?: string) {
  const formatString =
    dateTime.minutes() > 0
      ? `YYYY-MM-DD ${get(CDayOfWeek, dateTime.day())}요일 A h시 m분`
      : `YYYY-MM-DD ${get(CDayOfWeek, dateTime.day())}요일 A h시`;

  //기존 오전 12시로 표기
  //const formattingTime = dateTime.format(formatString).replace('PM', '오후').replace('AM', '오전');

  //수정 오전 0시로 표기
  const formattingTime = dateTime.format(formatString).includes('AM 12시')
    ? dateTime
        .format(formatString)
        .replace('PM', '오후')
        .replace('AM', '오전')
        .replace('12시', '0시')
    : dateTime
        .format(formatString)
        .replace('PM', '오후')
        .replace('AM', '오전');

  return formattingTime;
}

export function toLocaleTimeStringLocal(dateTime: Moment, timezone?: string) {
  const formatString = dateTime.minutes() > 0 ? `A h시 m분` : `A h시`;

  //기존 오전 12시로 표기
  //const formattingTime = dateTime.format(formatString).replace('PM', '오후').replace('AM', '오전').replace(':00', '시').replace(':30', '시30분')

  //수정 오전 0시로 표기
  const formattingTime = dateTime
    .format(formatString)
    .replace('PM', '오후')
    .replace('AM', '오전')
    .replace(':00', '시')
    .replace(':30', '시30분')
    .includes('오전 12시')
    ? dateTime
        .format(formatString)
        .replace('PM', '오후')
        .replace('AM', '오전')
        .replace(':00', '시')
        .replace(':30', '시30분')
        .replace('12시', '0시')
    : dateTime
        .format(formatString)
        .replace('PM', '오후')
        .replace('AM', '오전')
        .replace(':00', '시')
        .replace(':30', '시30분');

  return formattingTime;
}

//ex) MentoringClassCreate.tsx에서 selectedSchedules STATE의 각 요소들을 만들어주는 역할
//{time_slice: number, date_at: string} ==> momentTZ.Moment 형태로 리턴
export function getDateTimeFromSelectedTimeSlice(
  selectedTimeSlice: TSelectedTimeSlice,
  timezone: string,
): Moment {
  const value = momentTZ(get(selectedTimeSlice, 'date_at'), CDateFormat, timezone);
  const time = getTimeFromTimeSlice(get(selectedTimeSlice, 'time_slice'), timezone);

  value.set({
    hour: time.get('hour'),
    minute: time.get('minute'),
    second: 0,
  });

  return value;
}

ClassScheduleModal.defaultProps = {};

type TScheduleTableWrapperProps = {
  numOfSlice?: number;
};

export const ScheduleTableWrapper = styled.div<TScheduleTableWrapperProps>`
padding: 10px 0px;

.dashed .ant-table-cell {
  border-bottom: 1px dashed #f0f0f0;
}
.ant-table-cell {
  padding: 0px;
  text-align: center;
  font-size: 12px;
  cursor: default;
  height: ${CCellHeight}px;
  line-height: ${CCellHeight}px;
}

@media screen and (max-width: 480px){
  .ant-table-cell{
    font-size: 7px;
    white-space: nowrap;
  }
}

.ant-table-cell:hover {
  background: #fff;
}
.ant-table.ant-table-bordered > .ant-table-container {
  border: none;
}
.ant-table-tbody > tr.ant-table-row:hover > td {
  background: transparent;
}
.ant-table-tbody > tr.ant-table-row > td:hover:not(.${EScheduleStatus.UnAvailable}) {
  background: #fff;
}
.ant-table-cell .${EScheduleStatus.UnAvailable} {
  background: #f9f9f9;
  height: 100%;
  width: 100%;
}
.ant-table-cell .${EScheduleStatus.Pending} {
  background: #f9f9f9;
  height: 100%;
  width: 100%;
  color: rgb(174, 174, 174);
  border-radius: 4px;
}
.ant-table-cell .${EScheduleStatus.Reserved} {
  background: #dcdcdc;
  height: 100%;
  width: 100%;
  color: rgb(174, 174, 174);
  border-radius: 4px;
}
.ant-table-cell .${EScheduleStatus.Available} {
  border-radius: 4px;
  background: ${SecondaryColor};
  cursor: pointer;
  color: #fff;
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 10;
}

.${CActiveTableCellClass} {
  border-radius: 4px;
  position: absolute;
  top: 0;
  width: 100%;
  ${(props) => props.numOfSlice && `height:${CCellHeight * props.numOfSlice + 2}px`};
  z-index: 100;
  overflow-wrap: anywhere;
}
.ant-table-cell .${
  EScheduleStatus.Available
} + .${CActiveTableCellClass}:hover:not(${CDataSelectedClass}) { //선택 가능한 스케줄 중에서 (호버 && 클릭X) 한 부분
  background: #fff;
  box-shadow: 0 0 5px 2px ${PrimaryColor};
  cursor: pointer;
}

@media screen and (max-width: ${CNoMouseEventMaxModalsize}px){
  .ant-table-cell .${EScheduleStatus.Available} + .${CActiveTableCellClass}:hover:not(${CDataSelectedClass}) {
    background: none;
    box-shadow:none;
  }
}

//선택 가능한 스케줄 중에서 (호버 && 클릭O) 한 부분
.${CActiveTableCellClass}[${CDataSelectedClass}] {
  background: ${PrimaryColor};
  box-shadow: 0 0 5px 2px ${PrimaryColor};
  cursor: default;
  z-index: 200;
}


.ant-table-cell.ant-table-cell-scrollbar {
  opacity: 1;
}
.ant-table-body {
  margin-top: -${CCellHeight * 2}px;
}
.ant-table-thead > tr > th {
  background: #fff;
  opacity: 0.8;
  z-index: 1000;
  border-bottom: none;
}

.table-cell-preview {
  top: 8px;
  position: absolute;
  width: 100%;
  text-align: center;
  font-size: calc(0.4vw + 8px);
}

@media screen and (max-width: ${CNoMouseEventMaxModalsize}px){
  .table-cell-preview{
    height:100%;
    box-shadow: 0 0 5px 10px ${PrimaryColor};
  }
}

.row-border-none {
  border-bottom: none;
}
`;

export default ClassScheduleModal;
